import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { LanguageProvider } from "./i18n/LanguageContext";
import Layout from "./components/Layout";
import ScrollToTop from "./components/ScrollToTop";
import AuthCallback from "./components/AuthCallback";
import ProtectedRoute from "./components/ProtectedRoute";

// Eagerly loaded – on the critical path (first page users see)
import HomePage from "./pages/HomePage";

// Lazy loaded – only fetched when the route is first visited
const SearchPage                   = lazy(() => import("./pages/SearchPage"));
const MemorialPage                 = lazy(() => import("./pages/MemorialPage"));
const VIPPage                      = lazy(() => import("./pages/VIPPage"));
const EreignissePage               = lazy(() => import("./pages/EreignissePage"));
const EreignisDetailPage           = lazy(() => import("./pages/EreignisDetailPage"));
const LoginPage                    = lazy(() => import("./pages/LoginPage"));
const GoogleCallback               = lazy(() => import("./pages/GoogleCallback"));
const DashboardPage                = lazy(() => import("./pages/DashboardPage"));
const DirectoryPage                = lazy(() => import("./pages/DirectoryPage"));
const DirectoryRegisterPage        = lazy(() => import("./pages/DirectoryRegisterPage"));
const DirectoryEditPage            = lazy(() => import("./pages/DirectoryEditPage"));
const DirectoryPaymentSuccessPage  = lazy(() => import("./pages/DirectoryPaymentSuccessPage"));
const DirectoryPaymentCancelPage   = lazy(() => import("./pages/DirectoryPaymentCancelPage"));
const DirectoryUpgradePage         = lazy(() => import("./pages/DirectoryUpgradePage"));
const AdminPage                    = lazy(() => import("./pages/AdminPage"));
const StaticPageView               = lazy(() => import("./pages/StaticPageView"));
const ClaimRequestPage             = lazy(() => import("./pages/ClaimRequestPage"));
const MyDirectoryPage              = lazy(() => import("./pages/MyDirectoryPage"));
const DonatePage                   = lazy(() => import("./pages/DonatePage"));
const TrauerspruchGeneratorPage    = lazy(() => import("./pages/TrauerspruchGeneratorPage"));
const TrauerspruchFavoritenPage    = lazy(() => import("./pages/TrauerspruchFavoritenPage"));
const InseratErstellenPage         = lazy(() => import("./pages/InseratErstellenPage"));
const InseratEditPage              = lazy(() => import("./pages/InseratEditPage"));

// Set default page title
const DEFAULT_TITLE = "Verstorben.ch | Todesanzeigen und Gedenkportal Schweiz";

// Google Analytics Measurement ID
const GA_MEASUREMENT_ID = 'G-9K4EXDDLG1';

// Minimal loading fallback shown while a lazy chunk loads
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <div className="w-8 h-8 border-2 border-[#C5A059] border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function AppRouter() {
  const location = useLocation();

  // Track page views with Google Analytics on route change
  useEffect(() => {
    if (typeof window.gtag === 'function') {
      window.gtag('config', GA_MEASUREMENT_ID, {
        page_path: location.pathname + location.search,
        page_title: document.title
      });
    }
  }, [location]);

  // Set default title on route change (unless a page sets its own)
  useEffect(() => {
    const pagesWithOwnTitle = ['/gedenken/', '/todesanzeigen/', '/avis-de-deces/', '/annunci-funebri/', '/seite/', '/page/', '/pagina/'];
    const hasOwnTitle = pagesWithOwnTitle.some(p => location.pathname.includes(p));
    if (!hasOwnTitle) {
      document.title = DEFAULT_TITLE;
    }
  }, [location.pathname]);

  // Detect session_id in hash - process BEFORE ProtectedRoute runs
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Layout>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/suche" element={<SearchPage />} />
          <Route path="/persoenlichkeiten" element={<VIPPage />} />
          <Route path="/ereignisse" element={<EreignissePage />} />
          <Route path="/ereignisse/:slug" element={<EreignisDetailPage />} />
          {/* Legacy route (ID-based) - still supported for backwards compatibility */}
          <Route path="/gedenken/:id" element={<MemorialPage />} />
          {/* New SEO-friendly routes (slug-based) - multilingual */}
          <Route path="/todesanzeigen/:kanton/:slug" element={<MemorialPage />} />
          <Route path="/avis-de-deces/:kanton/:slug" element={<MemorialPage />} />
          <Route path="/annunci-funebri/:kanton/:slug" element={<MemorialPage />} />
          {/* Claim request page - supports both inserat and directory */}
          <Route path="/beanspruchen/:id" element={<ClaimRequestPage />} />
          <Route path="/verzeichnis/beanspruchen/:id" element={<ClaimRequestPage />} />
          {/* Static pages - multilingual */}
          <Route path="/seite/:slug" element={<StaticPageView />} />
          <Route path="/page/:slug" element={<StaticPageView />} />
          <Route path="/pagina/:slug" element={<StaticPageView />} />
          {/* Donation page */}
          <Route path="/spenden" element={<DonatePage />} />
          <Route path="/don" element={<DonatePage />} />
          <Route path="/donazione" element={<DonatePage />} />
          {/* Trauerspruch Generator */}
          <Route path="/trauerspruch-generator" element={<TrauerspruchGeneratorPage />} />
          <Route path="/trauerspruch-favoriten" element={<TrauerspruchFavoritenPage />} />
          <Route path="/kontakt" element={<Navigate to="/seite/kontakt" replace />} />
          <Route
            path="/todesanzeige-erfassen"
            element={
              <ProtectedRoute>
                <InseratErstellenPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inserat/:id/bearbeiten"
            element={
              <ProtectedRoute>
                <InseratEditPage />
              </ProtectedRoute>
            }
          />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth/google/callback" element={<GoogleCallback />} />
          <Route path="/verzeichnis" element={<DirectoryPage />} />
          <Route
            path="/verzeichnis/eintragen"
            element={
              <ProtectedRoute>
                <DirectoryRegisterPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/verzeichnis/bearbeiten/:id"
            element={
              <ProtectedRoute>
                <DirectoryEditPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/verzeichnis/upgrade/:id"
            element={
              <ProtectedRoute>
                <DirectoryUpgradePage />
              </ProtectedRoute>
            }
          />
          <Route path="/verzeichnis/zahlung-erfolgreich" element={<DirectoryPaymentSuccessPage />} />
          <Route path="/verzeichnis/zahlung-abgebrochen" element={<DirectoryPaymentCancelPage />} />
          <Route
            path="/mein-verzeichnis"
            element={
              <ProtectedRoute>
                <MyDirectoryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/mein-bereich"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminPage />
              </ProtectedRoute>
            }
          />
        </Routes>
      </Suspense>
    </Layout>
  );
}

function App() {
  return (
    <LanguageProvider>
      <BrowserRouter>
        <ScrollToTop />
        <AppRouter />
      </BrowserRouter>
    </LanguageProvider>
  );
}

export default App;
